import React, { useEffect } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-light-svg-icons";

function ImageViewer({ images }) {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const selectedImage = images[selectedIndex];
  const bigimage = selectedImage?.replace(/\/500x500\//, "/350x350/");
  const hasMultiple = images.length > 1;

  useEffect(() => {
    setSelectedIndex(0);
  }, [images]);

  const goPrev = React.useCallback(() => {
    if (!hasMultiple) return;
    setSelectedIndex((i) => (i - 1 + images.length) % images.length);
  }, [hasMultiple, images.length]);

  const goNext = React.useCallback(() => {
    if (!hasMultiple) return;
    setSelectedIndex((i) => (i + 1) % images.length);
  }, [hasMultiple, images.length]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext]);

  const isBigImageVideo = selectedImage?.includes("youtube");
  const videoId = isBigImageVideo
    ? selectedImage.split("v=")[1].split("&")[0]
    : null;

  return (
    <Container>
      <Miniaturas>
        {images.map((image, index) => {
          const isVideo = image.includes("youtube");
          if (isVideo) {
            const thumbVideoId = image.split("v=")[1].split("&")[0];
            return (
              <Minatura
                key={index}
                src={`https://img.youtube.com/vi/${thumbVideoId}/0.jpg`}
                selected={index === selectedIndex}
                alt={`Thumbnail ${index}`}
                onClick={() => setSelectedIndex(index)}
              />
            );
          }
          return (
            <Minatura
              key={index}
              src={image.replace(/\/500x500\//, "/70x70/")}
              selected={index === selectedIndex}
              alt={`Thumbnail ${index}`}
              onClick={() => setSelectedIndex(index)}
            />
          );
        })}
      </Miniaturas>
      <Imagem>
        {isBigImageVideo ? (
          <iframe
            width="100%"
            height="350"
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video player"
            frameBorder="0"
            allowFullScreen
          ></iframe>
        ) : (
          <img src={bigimage} className="img-fluid" alt="Selected" />
        )}
        {hasMultiple && (
          <>
            <CaretButton $side="left" onClick={goPrev} aria-label="Anterior">
              <FontAwesomeIcon icon={faChevronLeft} />
            </CaretButton>
            <CaretButton $side="right" onClick={goNext} aria-label="Proximo">
              <FontAwesomeIcon icon={faChevronRight} />
            </CaretButton>
          </>
        )}
      </Imagem>
    </Container>
  );
}

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  height: 100%;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
  }
`;

const Miniaturas = styled.div`
  grid-area: 1 / 1 / 2 / 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;

  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    grid-area: 1 / 1 / 2 / 2;
  }
`;

const Minatura = styled.img`
  height: 50px;
  width: 70px;
  cursor: pointer;
  margin: 5px;
  border: ${(props) =>
    props.selected ? "2px solid #0091ff" : "2px solid transparent"};
  object-fit: cover;
  border-radius: 5px;
`;

const Imagem = styled.div`
  grid-area: 1 / 2 / 2 / 6;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  @media (max-width: 768px) {
    grid-area: 2 / 1 / 3 / 2;
  }

  & img {
    max-width: 100%;
    max-height: 350px;
    object-fit: cover;
    border-radius: 10px;
  }
`;

const CaretButton = styled.button`
  position: absolute;
  top: 50%;
  ${(props) => (props.$side === "left" ? "left: 8px;" : "right: 8px;")}
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  z-index: 2;
  transition: background 0.15s, transform 0.15s;

  &:hover {
    background: #fff;
    transform: translateY(-50%) scale(1.08);
  }
`;

export default ImageViewer;
